import type { PropertyDescriptor, TransitionDefinition } from '../core/definitions';
import type { SettingsBag } from '../core/model';
import { EASING_OPTIONS, ease } from './easing';
import { num, parseColor } from './shaderWrap';

const easingProperty: PropertyDescriptor = { type: 'select', key: 'easing', label: 'Easing', options: EASING_OPTIONS };

const easing = (t: number, settings: SettingsBag): number => ease(settings.easing, t);

const DIRECTIONS: Record<string, [number, number]> = {
  left: [-1, 0],
  right: [1, 0],
  up: [0, -1],
  down: [0, 1],
};

const directionProperty: PropertyDescriptor = {
  type: 'select',
  key: 'direction',
  label: 'Direction',
  options: [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'up', label: 'Up' },
    { value: 'down', label: 'Down' },
  ],
};

/** Unit vector the pictures travel along, in uv space (y down). */
function direction(settings: SettingsBag): [number, number] {
  return DIRECTIONS[String(settings.direction)] ?? DIRECTIONS.left!;
}

const INSIDE = `
bool inside(vec2 uv) {
  return uv.x >= 0.0 && uv.y >= 0.0 && uv.x <= 1.0 && uv.y <= 1.0;
}
`;

export const cut: TransitionDefinition = {
  kind: 'cut',
  displayName: 'Cut',
  defaults: () => ({}),
  properties: () => [],
  fragment: null,
};

export const fade: TransitionDefinition = {
  kind: 'fade',
  displayName: 'Fade',
  defaults: () => ({ easing: 'linear' }),
  properties: () => [easingProperty],
  fragment: `
void main() {
  fragColor = mix(texture(uFrom, vUv), texture(uTo, vUv), uProgress);
}
`,
  easing,
};

export const fadeToColor: TransitionDefinition = {
  kind: 'fade-to-color',
  displayName: 'Fade to Color',
  defaults: () => ({ color: '#000000', midpoint: 0.5, easing: 'linear' }),
  properties: () => [
    { type: 'color', key: 'color', label: 'Color' },
    { type: 'number', key: 'midpoint', label: 'Peak color point', min: 0.05, max: 0.95, step: 0.01, slider: true },
    easingProperty,
  ],
  fragment: `
uniform vec3 uFadeColor;
uniform float uMidpoint;
void main() {
  vec4 solid = vec4(uFadeColor, 1.0);
  if (uProgress < uMidpoint) {
    fragColor = mix(texture(uFrom, vUv), solid, uProgress / uMidpoint);
  } else {
    fragColor = mix(solid, texture(uTo, vUv), (uProgress - uMidpoint) / (1.0 - uMidpoint));
  }
}
`,
  uniforms: (settings) => ({
    uFadeColor: parseColor(settings.color).slice(0, 3),
    uMidpoint: Math.min(0.95, Math.max(0.05, num(settings.midpoint, 0.5))),
  }),
  easing,
  audio: 'cut-at-midpoint',
};

export const slide: TransitionDefinition = {
  kind: 'slide',
  displayName: 'Slide',
  defaults: () => ({ direction: 'left', easing: 'ease-in-out' }),
  properties: () => [directionProperty, easingProperty],
  // Both pictures travel together: the incoming one pushes the outgoing one out.
  fragment: `${INSIDE}
uniform vec2 uDirection;
void main() {
  vec2 fromUv = vUv - uDirection * uProgress;
  vec2 toUv = fromUv + uDirection;
  fragColor = inside(toUv) ? texture(uTo, toUv) : (inside(fromUv) ? texture(uFrom, fromUv) : vec4(0.0));
}
`,
  uniforms: (settings) => ({ uDirection: direction(settings) }),
  easing,
};

export const swipe: TransitionDefinition = {
  kind: 'swipe',
  displayName: 'Swipe',
  defaults: () => ({ direction: 'left', easing: 'ease-in-out' }),
  properties: () => [directionProperty, easingProperty],
  // The outgoing picture stays put while the incoming one slides over it.
  fragment: `${INSIDE}
uniform vec2 uDirection;
void main() {
  vec2 toUv = vUv + uDirection * (1.0 - uProgress);
  fragColor = inside(toUv) ? texture(uTo, toUv) : texture(uFrom, vUv);
}
`,
  uniforms: (settings) => ({ uDirection: direction(settings) }),
  easing,
};

const PATTERNS = ['linear', 'radial', 'clock', 'diamond', 'blinds'];

export const lumaWipe: TransitionDefinition = {
  kind: 'luma-wipe',
  displayName: 'Luma Wipe',
  defaults: () => ({ pattern: 'linear', angle: 0, softness: 0.05, invert: false, blinds: 8, easing: 'linear' }),
  properties: () => [
    {
      type: 'select',
      key: 'pattern',
      label: 'Pattern',
      options: [
        { value: 'linear', label: 'Linear' },
        { value: 'radial', label: 'Radial' },
        { value: 'clock', label: 'Clock' },
        { value: 'diamond', label: 'Diamond' },
        { value: 'blinds', label: 'Blinds' },
      ],
    },
    {
      type: 'number',
      key: 'angle',
      label: 'Angle',
      min: 0,
      max: 360,
      step: 1,
      unit: '°',
      slider: true,
      visibleWhen: (s) => s.pattern === 'linear' || s.pattern === 'clock' || s.pattern === 'blinds',
    },
    { type: 'number', key: 'blinds', label: 'Blinds', min: 2, max: 64, step: 1, visibleWhen: (s) => s.pattern === 'blinds' },
    { type: 'number', key: 'softness', label: 'Softness', min: 0, max: 1, step: 0.01, slider: true },
    { type: 'boolean', key: 'invert', label: 'Invert' },
    easingProperty,
  ],
  // A procedural gradient in 0..1 decides when each pixel switches: low
  // values first. Softness widens the switch into a blend band.
  fragment: `
uniform int uPattern;
uniform float uAngle;
uniform float uSoftness;
uniform float uInvert;
uniform float uBlinds;
const float TAU = 6.283185307179586;
float gradient(vec2 uv) {
  vec2 p = uv - 0.5;
  vec2 dir = vec2(cos(uAngle), sin(uAngle));
  // Projection on the wipe direction, scaled so the unit square maps exactly onto 0..1.
  float along = dot(p, dir) / (abs(dir.x) + abs(dir.y)) + 0.5;
  if (uPattern == 1) {
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    return length(p * aspect) / length(0.5 * aspect);
  }
  if (uPattern == 2) return fract(atan(p.x, -p.y) / TAU - uAngle / TAU + 1.0);
  if (uPattern == 3) return abs(p.x) + abs(p.y);
  if (uPattern == 4) return fract(along * uBlinds);
  return along;
}
void main() {
  float g = gradient(vUv);
  if (uInvert > 0.5) g = 1.0 - g;
  float band = max(uSoftness, 1e-4);
  float t = clamp((uProgress * (1.0 + band) - g) / band, 0.0, 1.0);
  fragColor = mix(texture(uFrom, vUv), texture(uTo, vUv), t);
}
`,
  uniforms: (settings) => ({
    uPattern: Math.max(0, PATTERNS.indexOf(String(settings.pattern))),
    uAngle: (num(settings.angle, 0) * Math.PI) / 180,
    uSoftness: Math.min(1, Math.max(0, num(settings.softness, 0.05))),
    uInvert: settings.invert === true ? 1 : 0,
    uBlinds: Math.max(1, Math.round(num(settings.blinds, 8))),
  }),
  easing,
};
